// Mobile-specific performance optimizations

// Debounce function for performance
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Throttle function for performance
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean = false

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Check if device is mobile
export const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

// Check if device supports touch
export const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  )
}

// Get device pixel ratio for high DPI displays
export const getDevicePixelRatio = (): number => {
  if (typeof window === 'undefined') return 1

  return window.devicePixelRatio || 1
}

// Check if device is low-end (basic performance optimization)
export const isLowEndDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false

  // Check for low-end device indicators
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection

  if (connection) {
    // Slow connection indicates potential low-end device
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      return true
    }
  }

  // Check for limited memory (if available)
  if ((navigator as any).deviceMemory && (navigator as any).deviceMemory < 2) {
    return true
  }

  // Check for limited CPU cores (if available)
  if ((navigator as any).hardwareConcurrency && (navigator as any).hardwareConcurrency < 4) {
    return true
  }

  return false
}

// Optimize for mobile performance
export const optimizeForMobile = () => {
  if (typeof window === 'undefined') return

  // Disable hover effects on touch devices
  if (isTouchDevice()) {
    document.body.classList.add('touch-device')
  }

  // Add mobile class for CSS optimizations
  if (isMobile()) {
    document.body.classList.add('mobile-device')
  }

  // Add low-end device class for performance optimizations
  if (isLowEndDevice()) {
    document.body.classList.add('low-end-device')
  }

  // Add high DPI class for retina displays
  if (getDevicePixelRatio() > 1) {
    document.body.classList.add('high-dpi')
  }
}

// Preload critical resources
export const preloadCriticalResources = () => {
  if (typeof document === 'undefined') return

  // Preload critical CSS
  const criticalCSS = document.createElement('link')
  criticalCSS.rel = 'preload'
  criticalCSS.href = '/src/styles/globals.css'
  criticalCSS.as = 'style'
  document.head.appendChild(criticalCSS)

  // Preload critical fonts (if any)
  // This would be used if we had custom fonts
}

// Initialize performance optimizations
export const initPerformanceOptimizations = () => {
  optimizeForMobile()
  preloadCriticalResources()
}
